/**
 * PrestigeAccessoryPanel - Accessory controls for Prestige model
 * Uses FaceID system with South0/1, North0/1 for dual-room
 */
import { useState } from 'react';
import {
  FaceID,
  PartType,
  PART_TYPE_NAMES,
  getPrestigeAccessoryCatalog,
} from '@infrastructure/three/prestigeService';
import { InfoTooltip } from './InfoTooltip';

type AccessoryCategory = 'Screens & Doors' | 'Walls & Panels';

const CATEGORY_LABELS: AccessoryCategory[] = ['Screens & Doors', 'Walls & Panels'];

const CATEGORY_PART_TYPES: Record<AccessoryCategory, number[]> = {
  'Screens & Doors': [
    PartType.SLIMLINE_FRAME_GLASS_DOOR,
    PartType.MOTORIZED_WIND_SCREEN,
  ],
  'Walls & Panels': [
    PartType.WPC_WALL,
    PartType.SLIDING_ADJUSTABLE_SHUTTER_PANEL,
  ],
};

const FACE_LABELS: Record<string, string> = {
  [FaceID.East]: 'Side 1',
  [FaceID.West]: 'Side 2',
  [FaceID.South0]: 'Side 3',
  [FaceID.South1]: 'Side 3',
  [FaceID.North0]: 'Side 4',
  [FaceID.North1]: 'Side 4',
};

type SurfacePart = {
  faceId: string;
  visible: boolean;
  partType: number | null;
};

type Props = {
  widthValue: number;
  roomCount: number;
  surfacePartList: SurfacePart[];
  onRemovePart: (faceId: string) => void;
  canAddPart: (faceId: string) => boolean;
  onCustomizePart: (faceId: string, partType: number) => void;
  activeAccessoryType: number | null;
  isAddMode: boolean;
  onSelectAccessory: (partType: number) => void;
  onExitAddMode: () => void;
  onResetAccessories: () => void;
};

export const PrestigeAccessoryPanel = ({
  widthValue,
  roomCount,
  surfacePartList,
  onRemovePart,
  canAddPart,
  onCustomizePart,
  activeAccessoryType,
  isAddMode,
  onSelectAccessory,
  onExitAddMode,
  onResetAccessories,
}: Props) => {
  const visibleFaces = [
    FaceID.East,
    FaceID.West,
    FaceID.South0,
    FaceID.North0,
    ...(roomCount >= 2 ? [FaceID.South1, FaceID.North1] : []),
  ];

  const [activeTab, setActiveTab] = useState(visibleFaces[0]);
  const [activeCategory, setActiveCategory] = useState<AccessoryCategory>('Screens & Doors');

  const prestigeAccessoryCatalog = getPrestigeAccessoryCatalog({ widthValue });

  const catalogForCategory = prestigeAccessoryCatalog.filter((item) =>
    CATEGORY_PART_TYPES[activeCategory].includes(item.partType)
  );

  const getPartForFace = (faceId: string): SurfacePart | undefined =>
    surfacePartList.find((p) => p.faceId === faceId);

  const getAccessoryName = (partType: number): string => {
    const item = prestigeAccessoryCatalog.find((a) => a.partType === partType);
    return item?.name || PART_TYPE_NAMES[partType] || `Part ${partType}`;
  };

  const hasAnyInstalled = visibleFaces.some((faceId) => {
    const part = getPartForFace(faceId);
    return part?.partType !== null && part?.partType !== undefined;
  });

  const activePart = getPartForFace(activeTab);
  const faceAvailable = canAddPart(activeTab);
  const faceHasPart =
    activePart?.partType !== null && activePart?.partType !== undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
      {/* ── Heading ── */}
      <p style={{ textAlign: 'center', fontSize: 15, color: '#444', margin: 0 }}>
        Choose your <strong style={{ fontWeight: 800 }}>sidewalls</strong>:
      </p>

      {/* ── Category buttons ── */}
      <div style={{ display: 'flex', gap: 8 }}>
        {CATEGORY_LABELS.map((category) => {
          const isActive = activeCategory === category;
          return (
            <button
              key={category}
              type="button"
              onClick={() => setActiveCategory(category)}
              style={{
                flex: 1,
                padding: '10px 8px',
                fontSize: 13,
                fontWeight: 500,
                color: isActive ? '#fff' : '#333',
                background: isActive ? '#393B42' : '#fff',
                border: '1.5px solid',
                borderColor: isActive ? '#393B42' : '#e0e0e0',
                borderRadius: 6,
                cursor: 'pointer',
                transition: 'background 0.2s, color 0.2s, border-color 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              {category}
            </button>
          );
        })}
      </div>

      {/* ── Catalog grid ── */}
      <div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
          }}
        >
          {catalogForCategory.map((item) => {
            const isSelected = activeAccessoryType === item.partType;
            return (
              <div key={item.partType} style={{ position: 'relative' }}>
                {/* ⓘ icon — outside the overflow:hidden button so tooltip is never clipped */}
                <span
                  style={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}
                  onClick={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  <InfoTooltip text={item.description} />
                </span>

                <button
                  type="button"
                  onClick={() => onSelectAccessory(item.partType)}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    border: isSelected
                      ? '2.5px solid #71903e'
                      : '1.5px solid #e0e0e0',
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: isSelected ? 'rgba(113,144,62,0.04)' : '#fff',
                    padding: 0,
                    transition: 'border-color 0.2s, background 0.2s',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      background: '#f0f2f5',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src={item.imgSrc}
                      alt={item.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </div>
                  <div style={{ padding: '8px 10px' }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: isSelected ? 700 : 400,
                        color: isSelected ? '#333' : '#444',
                        lineHeight: 1.3,
                        textAlign: 'center',
                      }}
                    >
                      {item.name}
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>

        {!isAddMode && (
          <div
            style={{
              textAlign: 'center',
              fontSize: 12,
              color: '#aaa',
              marginTop: 8,
            }}
          >
            Tap an accessory to start placing it.
          </div>
        )}
      </div>

      {/* ── Add-mode banner ── */}
      {isAddMode && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            borderRadius: 8,
            background: 'rgba(113,144,62,0.08)',
            border: '1px solid rgba(113,144,62,0.25)',
          }}
        >
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#71903e',
              flexShrink: 0,
              animation: 'pulse 1.5s infinite',
            }}
          />
          <span
            style={{ flex: 1, fontSize: 13, color: '#444', lineHeight: 1.4 }}
          >
            Tap a <strong>+ icon</strong> in the scene to place
          </span>
          <button
            type="button"
            onClick={onExitAddMode}
            style={{
              padding: '4px 14px',
              fontSize: 12,
              fontWeight: 600,
              color: '#71903e',
              background: '#fff',
              border: '1px solid #71903e',
              borderRadius: 16,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Separator ── */}
      <div style={{ height: 1, background: '#eee' }} />

      {/* ── Face tabs ── */}
      <div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#444',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            marginBottom: 10,
          }}
        >
          Installed
        </div>

        <div
          style={{
            display: 'flex',
            borderBottom: '2px solid #eee',
            gap: 0,
          }}
        >
          {visibleFaces.map((faceId) => {
            const isActive = activeTab === faceId;
            const part = getPartForFace(faceId);
            const hasPart =
              part?.partType !== null && part?.partType !== undefined;
            return (
              <button
                key={faceId}
                type="button"
                onClick={() => setActiveTab(faceId)}
                style={{
                  flex: 1,
                  padding: '8px 4px',
                  fontSize: 12,
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? '#71903e' : '#888',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: isActive
                    ? '2px solid #71903e'
                    : '2px solid transparent',
                  marginBottom: -2,
                  cursor: 'pointer',
                  transition: 'color 0.2s',
                  position: 'relative',
                }}
              >
                {FACE_LABELS[faceId]}
                {hasPart && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: '#71903e',
                      marginLeft: 4,
                      verticalAlign: 'middle',
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div style={{ padding: '14px 0 4px' }}>
          {!faceAvailable && (
            <div
              style={{
                textAlign: 'center',
                fontSize: 13,
                color: '#aaa',
                padding: '16px 0',
              }}
            >
              Face not available (wall mounted)
            </div>
          )}

          {faceAvailable && faceHasPart && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #e8e8e8',
                background: '#fafafa',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
                  {getAccessoryName(activePart!.partType!)}
                </div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 1 }}>
                  Installed on {FACE_LABELS[activeTab]}
                </div>
              </div>

              <button
                type="button"
                onClick={() =>
                  onCustomizePart(activeTab, activePart!.partType!)
                }
                style={{
                  padding: '4px 12px',
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#555',
                  background: '#fff',
                  border: '1px solid #d9d9d9',
                  borderRadius: 14,
                  cursor: 'pointer',
                }}
              >
                Edit
              </button>

              <button
                type="button"
                onClick={() => onRemovePart(activeTab)}
                aria-label={`Remove accessory from ${FACE_LABELS[activeTab]}`}
                style={{
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: '1px solid #e0e0e0',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  color: '#999',
                  fontSize: 14,
                  padding: 0,
                  flexShrink: 0,
                  transition: 'color 0.2s, border-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#e04848';
                  e.currentTarget.style.borderColor = '#e04848';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#999';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
              >
                &times;
              </button>
            </div>
          )}

          {faceAvailable && !faceHasPart && (
            <div
              style={{
                textAlign: 'center',
                fontSize: 13,
                color: '#aaa',
                padding: '16px 0',
              }}
            >
              No accessory on this face
            </div>
          )}
        </div>
      </div>

      {/* ── Reset button ── */}
      {hasAnyInstalled && (
        <button
          type="button"
          onClick={onResetAccessories}
          style={{
            width: '100%',
            padding: '10px 0',
            fontSize: 13,
            fontWeight: 500,
            color: '#e04848',
            background: 'transparent',
            border: '1px solid #e8e8e8',
            borderRadius: 8,
            cursor: 'pointer',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(224,72,72,0.04)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          Reset all accessories
        </button>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default PrestigeAccessoryPanel;
